'use client'
import { PortableText } from '@portabletext/react'
import { getPortableTextComponents } from 'app/components/PortableTextComponents'
import { slugifyHeading } from 'lib/util/slugifyHeading'
import { useState } from 'react'
import { isPortableTextTextBlock, PortableTextBlock } from 'sanity'
import { z } from 'zod'

import { articlePageSchema } from '@/lib/zodSchemas/articlePageSchema'
import { usePathname } from 'next/navigation'
import Breadcrumb from './Breadcrumb'

const getHeadings = (blocks: PortableTextBlock[]) => {
  return blocks
    .filter(isPortableTextTextBlock)
    .filter((block) => block.style === 'h3' || block.style === 'h4')
    .map((block) => {
      const text = block.children.map((child) => child.text).join()
      return {
        text,
        slug: slugifyHeading(text),
      }
    })
}

interface Props {
  article: z.infer<typeof articlePageSchema>
}

type Section = 'body' | 'goodToKnow' | 'context' | 'examples' | 'international'

const translations = {
  fi: {
    specificAdvice: 'Erityinen Ohje',
    goodToKnow: 'Hyvä tietää',
    context: 'Tausta',
    examples: 'Esimerkit ja Tapaukset',
    readMore: 'Lue lisää',
    international: 'Kansainväliset Velvoitteet',
    relatedTopics: 'Aiheeseen liittyviä aiheita',
    references: 'Lähteet',
    onThisPage: 'Sisällys',
  },
  nb: {
    specificAdvice: 'Spesifikke råd',
    goodToKnow: 'Greit å vite',
    context: 'Bakgrunn',
    examples: 'Eksempler og saker',
    readMore: 'Les mer',
    international: 'Internasjonale forpliktelser',
    relatedTopics: 'Relaterte temaer',
    references: 'Kilder',
    onThisPage: 'På denne siden',
  },
  en: {
    specificAdvice: 'Specific Advice',
    goodToKnow: 'Good to Know',
    context: 'Context',
    examples: 'Examples and Cases',
    readMore: 'Learn more',
    international: 'International Obligations',
    relatedTopics: 'Related topics',
    references: 'References',
    onThisPage: 'On this page',
  },
}

export function ArticleBody({ article }: Props) {
  const [headingInView, setHeadingInView] = useState<string | undefined>(
    undefined,
  )
  const handleInView = (inView: boolean, entry: IntersectionObserverEntry) => {
    if (inView) {
      setHeadingInView(entry.target.children[0].id)
    }
  }

  const t =
    translations[article.language as keyof typeof translations] ||
    translations.en

  const sections = [
    { name: 'body', title: t.specificAdvice },
    { name: 'goodToKnow', title: t.goodToKnow },
    { name: 'context', title: t.context },
    { name: 'examples', title: t.examples },
    { name: 'readMore', title: t.readMore },
  ]

  const pathname = usePathname()
  return (
    <div className="relative min-h-full flex-1 border-l border-green-100">
      <Breadcrumb path={pathname} />
      <div className=" flex items-center justify-center min-h-screen">
        <div className="max-w-6xl w-full p-6  grid grid-cols-12 gap-8">
          {/* Prose */}
          <div className="relative col-span-12 lg:col-span-12">
            <header className="mb-10 border-b border-green-200 pb-4">
              <h1 className="mb-2 text-2xl font-semibold md:text-3xl lg:text-4xl">
                {article.title}
              </h1>
              <p className="text-lg text-green-900 lg:text-xl">
                {article.description}
              </p>
            </header>
            {sections.map(
              (section) =>
                article[section.name as Section] && (
                  <div
                    key={section.name}
                    className={
                      section.name === 'examples'
                        ? 'bg-green-50 p-3 mt-1 rounded-xl'
                        : ''
                    }
                  >
                    <h3
                      className={`${section.name === 'examples' ? '' : 'mt-8'} mb-4 text-xl font-semibold md:text-2xl lg:text-3xl`}
                    >
                      {section.title}
                    </h3>
                    <PortableText
                      value={article[section.name as Section] || []}
                      components={getPortableTextComponents(handleInView)}
                    />
                  </div>
                ),
            )}
            {article.international && (
              <div className=" mt-8 pb-3 cursor-pointer font-bold">
                <details>
                  <summary className="mb-2 text-l font-semibold md:text-xl lg:text-2xl">
                    {t.international}
                  </summary>
                  <PortableText
                    value={article.international || []}
                    components={getPortableTextComponents(handleInView)}
                  />
                </details>
              </div>
            )}
            {article.relatedTopics && article.relatedTopics.length > 0 && (
              <div className="mt-8">
                <h3 className="mb-2 text-l font-semibold md:text-xl lg:text-2xl">
                  {t.relatedTopics}
                </h3>
                <div className="flex flex-wrap">
                  {article.relatedTopics.map((topic) => (
                    <div key={topic.slug} className="m-1">
                      <a
                        href={`/${topic.category.slug}/${topic.slug}`}
                        className="inline-block bg-green-100 text-green-800 text-sm font-semibold py-2 px-4 rounded-full hover:bg-green-200"
                      >
                        {topic.title}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {article.publications && article.publications.length > 0 ? (
              <div className="mt-8">
                <details>
                  <summary className="cursor-pointer mb-2 text-l font-semibold md:text-xl lg:text-2xl">
                    {t.references}
                  </summary>
                  {article.publications.map((publication) => (
                    <div key={publication.refNumber}>
                      <a
                        href={publication.url || ''}
                        style={{ wordWrap: 'break-word' }}
                      >
                        {publication.title}
                      </a>
                      <hr style={{ marginBottom: '10px', marginTop: '10px' }} />
                    </div>
                  ))}
                </details>
              </div>
            ) : (
              ''
            )}
          </div>
          {article.body && getHeadings(article.body).length > 0 && (
            <div className="sticky top-32 col-span-3 hidden h-[calc(100vh-8rem)] overflow-y-auto lg:block">
              <div className="border-l border-green-100 pl-4">
                <h4 className="mb-2 text-sm font-medium uppercase">
                  {t.onThisPage}
                </h4>
                <ul>
                  {article.body &&
                    getHeadings(article.body).map((heading) => (
                      <li
                        key={heading.slug}
                        className={`mb-2  transition-all hover:text-green-500 ${
                          headingInView === heading.slug ? 'font-semibold' : ''
                        }`}
                      >
                        <a className="" href={`#${heading.slug}`}>
                          {heading.text}
                        </a>
                      </li>
                    ))}
                  {article.readMore &&
                    getHeadings(article.readMore).map((heading) => (
                      <li
                        key={heading.slug}
                        className={`mb-2  transition-all hover:text-green-500 ${
                          headingInView === heading.slug ? 'font-semibold' : ''
                        }`}
                      >
                        <a className="" href={`#${heading.slug}`}>
                          {heading.text}
                        </a>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
